/**
 * Browser side of procedure (SOP) import/export: file download, clipboard, file read.
 *
 * The format itself — the bundle envelope, which fields travel, and what import
 * resolves — lives in `@erp/rpc` (`sop-bundle.contract.ts`), shared with the
 * backend so the detail page's Export and the `importSopBundle` procedure agree
 * without a translation layer. Anything about *what* a bundle contains belongs there.
 */

import {
  buildSopBundle,
  parseSopBundle,
  type AgentSopDto,
  type SopBundle,
} from '@erp/rpc';
import {
  downloadJsonText,
  parseJsonText,
  readJsonFile,
  summarizeValidationErrors,
} from 'src/lib/readJsonFile';

function buildExportJson(sop: AgentSopDto): string {
  return JSON.stringify(buildSopBundle(sop), null, 2);
}

/** `<slug>.v<version>.sop.json` — the version suffix names the file, it is not in the bundle. */
export function sopExportFileName(sop: Pick<AgentSopDto, 'name' | 'version'>) {
  const safeName = sop.name.replace(/[^a-zA-Z0-9_-]/g, '_') || 'procedure';
  return `${safeName}.v${sop.version}.sop.json`;
}

/** Export a procedure as a downloadable JSON file. */
export function exportSopToJson(sop: AgentSopDto) {
  downloadJsonText(sopExportFileName(sop), buildExportJson(sop));
}

/** Copy the procedure's bundle JSON to the clipboard. */
export async function copySopJsonToClipboard(sop: AgentSopDto): Promise<void> {
  await navigator.clipboard.writeText(buildExportJson(sop));
}

type ParsedBundle =
  { ok: true; bundle: SopBundle } | { ok: false; error: string };

function validateBundle(raw: unknown): ParsedBundle {
  const parsed = parseSopBundle(raw);
  if (!parsed.ok) {
    return {
      ok: false,
      error: `Invalid procedure file: ${summarizeValidationErrors(parsed.errors)}`,
    };
  }
  return { ok: true, bundle: parsed.bundle };
}

/** Parse pasted/loaded text into a bundle, or a single readable error message. */
export function parseSopBundleText(text: string): ParsedBundle {
  const read = parseJsonText(text);
  return read.ok ? validateBundle(read.raw) : read;
}

/** Read a `.json` file into a validated bundle, or an error message for the dialog. */
export async function readSopBundleFile(file: File): Promise<ParsedBundle> {
  const read = await readJsonFile(file);
  return read.ok ? validateBundle(read.raw) : read;
}

/** How a bundle's recorded provenance differs from the procedure it is about to become a draft
 * of. A bundle without a `source` (hand-written) has nothing to compare, so it differs in neither. */
export function bundleSourceMismatch(
  bundle: SopBundle,
  target: Pick<AgentSopDto, 'id' | 'organizationId'>,
): { otherProcedure: boolean; otherOrganization: boolean } {
  const source = bundle.source;
  return {
    otherProcedure: source?.sopId != null && source.sopId !== target.id,
    otherOrganization:
      source?.organizationId != null &&
      source.organizationId !== target.organizationId,
  };
}
